// Claude: RFXPublish.tsx — buyer publishes or edits a discover requirement 31-03-2026
// Claude: departments lifted to Discover.tsx; form reordered Filters>Tags>Dept>RFP; count shown above publish button 08-May-2026
import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Textarea,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
} from "@chakra-ui/react";
import { MdTune, MdLabel, MdInfoOutline } from "react-icons/md";
import { DatePicker, Select as AntSelect } from "antd";
import dayjs from "dayjs";
import { RfxAPI } from "../../Apis/rfxAPI";
import { useToastWrapper } from "../../Wrapper/toastWrapper";
import { useSelector } from "react-redux";

interface RFXPublishProps {
  filters: { targetCountry: string[]; targetIndustries: string[]; targetRelevanceStatus: string[] };
  selectedTags: string[];
  filtersActive: boolean;
  onOpenFilterDrawer: () => void;
  onOpenTagsDrawer: () => void;
  onPublished: (planExpired?: boolean) => void;
  editingRfx?: any;
  onEditDone?: () => void;
  onCancelEdit?: () => void;
  isPlanActive?: boolean;
  // Claude: lifted from local state — departments now managed by Discover.tsx for count wiring 08-May-2026
  departments: string[];
  onDepartmentsChange: (v: string[]) => void;
  departmentOptions: { label: string; value: string }[];
  reachCount: number | null;
  reachLoading: boolean;
}


const MAX_DAYS = 15;

const disabledDate = (current: dayjs.Dayjs) => {
  if (!current) return false;
  const today = dayjs().startOf("day");
  const maxDate = today.add(MAX_DAYS, "day");
  return current.isBefore(today) || current.isAfter(maxDate);
};

const RFXPublish = ({
  filters, selectedTags, filtersActive,
  onOpenFilterDrawer, onOpenTagsDrawer, onPublished,
  editingRfx, onEditDone, onCancelEdit, isPlanActive,
  departments, onDepartmentsChange, departmentOptions,
  reachCount, reachLoading,
}: RFXPublishProps) => {
  const { success, error, info } = useToastWrapper();
  const { user } = useSelector((state: any) => state.user);
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const [requirementText, setRequirementText] = useState("");
  const [expiryDate, setExpiryDate] = useState<dayjs.Dayjs>(dayjs().add(MAX_DAYS, "day"));
  const [isPublishing, setIsPublishing] = useState(false);
  // Claude: content-based preview count — fetched when modal opens 08-May-2026
  const [previewCount, setPreviewCount] = useState<number | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const isEdit = !!editingRfx;
  // Claude: isRepublish triggered by matchedCount (any matched seller) — fairer to sellers than responseCount 02-Apr-2026
  const isRepublish = isEdit && (editingRfx?.matchedCount ?? 0) > 0;

  // Claude: pre-fill form when editing an existing RFX 31-03-2026
  useEffect(() => {
    if (editingRfx) {
      setRequirementText(editingRfx.rfxDetails ?? "");
      setExpiryDate(editingRfx.expiryDate ? dayjs(editingRfx.expiryDate) : dayjs().add(MAX_DAYS, "day"));
      onDepartmentsChange(
        (editingRfx.departments || []).map((id: any) =>
          typeof id === "object" ? String(id._id) : String(id)
        )
      );
    } else {
      setRequirementText("");
      setExpiryDate(dayjs().add(MAX_DAYS, "day"));
      onDepartmentsChange([]);
    }
  }, [editingRfx]);

  const handlePublishClick = () => {
    // Claude: provisional buyer cannot publish RFX 13-04-2026
    if (user?.verificationStatus === "provisional") {
      error("Profile is pending review and has provisional access only.");
      return;
    }
    if (!requirementText.trim()) {
      error("Please describe your requirement.");
      return;
    }
    // Claude: department is optional 22-05-2026
    // Claude: fire preview count + open modal simultaneously 08-May-2026
    setPreviewCount(null);
    setPreviewLoading(true);
    onConfirmOpen();
    RfxAPI.getPreviewCount({
      rfxDetails: requirementText.trim(),
      targetCountry: filters.targetCountry,
      targetIndustries: filters.targetIndustries,
      targetTags: selectedTags,
      departments,
    })
      .then((res: any) => setPreviewCount(res?.data?.count ?? res?.count ?? 0))
      .catch(() => setPreviewCount(-1))
      .finally(() => setPreviewLoading(false));
  };

  const handleConfirm = async () => {
    setIsPublishing(true);
    try {
      const payload = {
        rfxDetails: requirementText.trim(),
        expiryDate: expiryDate.toISOString(),
        targetCountry: filters.targetCountry,
        targetIndustries: filters.targetIndustries,
        targetTags: selectedTags,
        departments,
      };

      if (!isEdit) {
        const res = await RfxAPI.createRfx({ ...payload });
        success("Requirement published. Matching in progress.");
        const planExpired = res?.planExpired ?? false;
        if (planExpired) info("Your RFX publish limit has been reached. Please contact support to renew your subscription.");
        setRequirementText("");
        setExpiryDate(dayjs().add(MAX_DAYS, "day"));
        onDepartmentsChange([]);
        onConfirmClose();
        onEditDone?.();
        onPublished(planExpired);
        return;
      } else if (!isRepublish) {
        await RfxAPI.editRfx(editingRfx._id, payload);
        success("Requirement updated.");
      } else {
        await RfxAPI.createRfx({ ...payload, republishedFromId: editingRfx._id });
        success("New requirement published.");
      }

      setRequirementText("");
      setExpiryDate(dayjs().add(MAX_DAYS, "day"));
      onDepartmentsChange([]);
      onConfirmClose();
      onEditDone?.();
      onPublished();
    } catch (e: any) {
      error(e?.message ?? "Failed. Please try again.");
    } finally {
      setIsPublishing(false);
    }
  };

  const buttonLabel = !isEdit ? "Publish Requirement" : isRepublish ? "Publish as New" : "Submit";
  const modalTitle = !isEdit ? "Review Your Requirement" : isRepublish ? "Publish as New Requirement" : "Review Changes";
  const modalConfirmLabel = !isEdit ? "Continue to Publish" : isRepublish ? "Publish as New" : "Submit";

  // Claude: plan expired — replace form with locked message 20-04-2026
  if (isPlanActive === false) {
    return (
      <Box mt={6} px={2} py={4} bg="#FFF3E0" borderRadius="10px" border="1px solid #FFCC80" textAlign="center">
        <Text fontSize="sm" color="#E65100">
          New requirements cannot be published — your subscription publish limit has been reached.
          Please contact support to renew.
        </Text>
      </Box>
    );
  }

  // Claude: provisional buyer — replace form with message 13-04-2026
  if (user?.verificationStatus === "provisional") {
    return (
      <Box mt={6} px={2}>
        <Text fontSize="sm" color="#C62828" textAlign="center">
          Unable to create a query as the profile is in provisional status and admin review is ongoing.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      {/* Republish warning */}
      {isRepublish && (
        <Flex mb={3} p={3} bg="#FFF8E1" borderRadius="8px" border="1px solid #FFE082"
          alignItems="flex-start" gap={2}>
          <Box color="orange.500" flexShrink={0} mt="1px"><MdInfoOutline size={14} /></Box>
          <Text fontSize="11px" color="orange.700" lineHeight="1.5">
            This requirement is already matched to sellers. Your changes will create a new requirement —
            the current one will be marked as republished.
          </Text>
        </Flex>
      )}

      {/* Claude: steps 1 & 2 — Filters + Tags side by side 08-May-2026 */}
      <Flex gap={2} mb={3}>
        <Button
          flex={1} size="sm" variant="outline"
          borderColor={filtersActive ? "#1565C0" : "gray.200"}
          color={filtersActive ? "#1565C0" : "gray.500"}
          borderRadius="8px" fontSize="12px" fontWeight={500}
          leftIcon={<MdTune size={15} />}
          _hover={{ bg: "#E3F2FD", borderColor: "#1565C0", color: "#1565C0" }}
          onClick={onOpenFilterDrawer}
        >
          Apply filters
          {filtersActive && (
            <Box as="span" ml={1} w="6px" h="6px" borderRadius="full" bg="#C62828" display="inline-block" />
          )}
        </Button>

        <Button
          flex={1} size="sm" variant="outline"
          borderColor={selectedTags.length > 0 ? "#1565C0" : "gray.200"}
          color={selectedTags.length > 0 ? "#1565C0" : "gray.500"}
          borderRadius="8px" fontSize="12px" fontWeight={500}
          leftIcon={<MdLabel size={15} />}
          _hover={{ bg: "#E3F2FD", borderColor: "#1565C0", color: "#1565C0" }}
          onClick={onOpenTagsDrawer}
        >
          Select tags
          {selectedTags.length > 0 && (
            <Box as="span" ml={2} bg="#1565C0" color="white" borderRadius="full"
              fontSize="10px" px={1.5} py={0} lineHeight="18px">
              {selectedTags.length}
            </Box>
          )}
        </Button>
      </Flex>

      {/* Claude: step 3 — Department; state lifted to Discover.tsx to wire reach count 08-May-2026 */}
      <Box mb={3}>
        <Text fontSize="10px" color="gray.500" mb={2}>
          Select which department in your organization is relevant for this requirement. This helps in matching with the right solution providers.{" "}
          <Text as="span" fontStyle="italic">Hint: if you are looking for an IT solution for your HR department, you can choose to add both IT and HR, in department to broaden the scope of seller match.</Text>
        </Text>
        <AntSelect
          mode="multiple"
          showSearch
          optionFilterProp="label"
          style={{ width: "100%" }}
          placeholder="Select department(s)"
          options={departmentOptions}
          value={departments}
          onChange={(v: string[]) => onDepartmentsChange(v)}
        />
      </Box>

      {/* Claude: step 4 — RFP Content 08-May-2026 */}
      <Textarea
        placeholder="Describe your requirement..."
        value={requirementText}
        onChange={(e) => setRequirementText(e.target.value)}
        rows={5}
        fontSize="sm"
        borderRadius="10px"
        focusBorderColor="#C9BFF5"
        resize="vertical"
        mb={3}
        bg="white"
      />

      {/* Expiry date */}
      <Box mb={3}>
        <Text fontSize="11px" color="gray.500" mb={1}>Expiry Date (max {MAX_DAYS} days, default: today + {MAX_DAYS})</Text>
        <DatePicker
          style={{ width: "100%", borderRadius: "8px" }}
          disabledDate={disabledDate}
          value={expiryDate}
          onChange={(date) => setExpiryDate(date ?? dayjs().add(MAX_DAYS, "day"))}
          format="DD MMM YYYY"
          placeholder="Select expiry date"
        />
      </Box>

      {/* Claude: persistent seller reach count — updates on filters/tags/department change 08-May-2026 */}
      <Box mb={3} p={2} bg="blue.50" borderRadius="8px" border="1px solid" borderColor="blue.100">
        <Text fontSize="sm" color="blue.700">
          {reachLoading
            ? "Estimating reach..."
            : reachCount === -1
            ? "Could not estimate reach."
            : reachCount !== null
            ? `Potentially reaching up to ${reachCount} seller${reachCount !== 1 ? "s" : ""} based on your filters.`
            : "—"}
        </Text>
      </Box>

      <Flex gap={2}>
        {isEdit && (
          <Button
            flex={1} size="md" variant="outline"
            borderRadius="10px" fontSize="13px" fontWeight={600}
            borderColor="gray.300" color="gray.500"
            _hover={{ borderColor: "#C62828", color: "#C62828" }}
            onClick={onCancelEdit}
          >
            Cancel
          </Button>
        )}
        <Button
          flex={1} size="md"
          bg="#0E122F" color="white"
          borderRadius="10px" fontSize="13px" fontWeight={600}
          _hover={{ bg: "#159300" }}
          onClick={handlePublishClick}
        >
          {buttonLabel}
        </Button>
      </Flex>

      {/* Confirmation modal — Claude: count removed from modal; now shown persistently above publish button 08-May-2026 */}
      <Modal isOpen={isConfirmOpen} onClose={onConfirmClose} isCentered size="sm">
        <ModalOverlay />
        <ModalContent mx={4} borderRadius="16px">
          <ModalHeader fontSize="md" fontWeight={700}>{modalTitle}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {/* Claude: content-based reach count — falls back to filter count on Gemini failure 08-May-2026 */}
            <Box mb={3} p={2} bg="blue.50" borderRadius="8px" border="1px solid" borderColor="blue.100">
              <Text fontSize="sm" color="blue.700">
                {previewLoading
                  ? "Estimating content-based reach..."
                  : previewCount !== null && previewCount !== -1
                  ? `Your requirement potentially matches ${previewCount} seller${previewCount !== 1 ? "s" : ""} based on content.`
                  : reachCount !== null && reachCount !== -1 && reachCount !== undefined
                  ? `Potentially reaching up to ${reachCount} seller${reachCount !== 1 ? "s" : ""} based on your filters.`
                  : "—"}
              </Text>
            </Box>
            <Box as="ul" pl={4} fontSize="sm" color="gray.700" lineHeight="1.7">
              <Box as="li" mb={1}>Each published RFX (enquiry or requirement or RFP) counts against your subscription plan limit.</Box>
              <Box as="li" mb={1}>Edits are only possible until no responses have been received. After that, you can only republish. Therefore, please check your requirements and filters prior to publishing.</Box>
              <Box as="li" mb={1}>Republishing an RFX also counts against your subscription limits.</Box>
              <Box as="li">Contact support@aftr.com for any clarifications.</Box>
            </Box>
          </ModalBody>
          <ModalFooter gap={3} flexWrap="wrap">
            <Button size="sm" variant="outline" onClick={onConfirmClose} isDisabled={isPublishing}>
              Cancel
            </Button>
            <Button size="sm" bg="#0E122F" color="white" _hover={{ bg: "#159300" }}
              isLoading={isPublishing} onClick={handleConfirm}>
              {modalConfirmLabel}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default RFXPublish;
